import { useState } from 'react';
import {
  dataExportResultSchema,
  type DataExportRequest,
  type ExportDomain,
} from '@forecastify/shared';
import type { Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { saveBlob } from '../file-helpers';
import { ExportDialog, saveCsv } from './export-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One line to give a screen an export. C17, decision T-10.                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A HOOK RATHER THAN A COPY OF THIS IN EVERY LIST.** T-10's whole point is one path; nine
 * screens each holding their own fetch, their own Blob and their own toast is nine chances to get the
 * filename or the error handling subtly different, and the mockup's nine separate arrows are what that
 * looks like when it happens.
 *
 * ⚠️ **The screen does not decide download-or-email.** The server does, on a row count only it has —
 * so this branches on the response rather than on anything the caller passed.
 */

let toastCounter = 0;

export interface ExportHandle {
  /** Render this. It is `null` until something opens the dialog. */
  readonly dialog: React.ReactNode;
  /** Open it for a domain. */
  /**
   * ⚠️ **C48 · `from`, `to`, `contractId` and `memberId` arrived for the Agent's export offer**, which
   * is a filter a model resolved out of *"August's hours on Acme"* — the dialog opens with it filled
   * in and the PERSON presses Export. Every existing caller passes none of them and behaves exactly
   * as before, including how the dialog re-seeds (see `ExportDialog`'s seed note).
   */
  readonly open: (
    domain: ExportDomain,
    options?: {
      reportId?: string;
      projectId?: string;
      ids?: readonly string[];
      from?: string;
      to?: string;
      contractId?: string;
      memberId?: string;
    },
  ) => void;
  /**
   * 17 Oct 2026 · run an export WITHOUT the dialog — a period's row menu, which already knows the
   * contract, the dates and the format. The same request, the same server path, the same toasts.
   */
  readonly run: (request: DataExportRequest) => void;
  /** Toasts to merge into the screen's own `Toaster`. */
  readonly toasts: readonly Toast[];
  /** ⚠️ `string | number`, because that is `Toaster`'s own id type — a narrower signature here
   * would make every caller cast at the one place the two lists are merged. */
  readonly dismiss: (id: string | number) => void;
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function useExport(): ExportHandle {
  const session = useSession();
  const t = useT();

  const [domain, setDomain] = useState<ExportDomain | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  /** ⚠️ A bulk action's selection. Null is "the whole list"; empty is a selection of nothing. */
  const [ids, setIds] = useState<readonly string[] | null>(null);
  /* ⚠️ C48 · the Agent's offer. Null for every other caller, which is what leaves the dialog's own
   * default range in charge. */
  const [seedFrom, setSeedFrom] = useState<string | null>(null);
  const [seedTo, setSeedTo] = useState<string | null>(null);
  const [contractId, setContractId] = useState<string | null>(null);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  /* ⚠️ Read once per mount: `exportRange` compares against it, and a value that changed mid-session
   * would relabel a range without anything having moved. */
  const [today] = useState(() => new Date().toISOString().slice(0, 10));

  const toast = (entry: Omit<Toast, 'id'>): void => {
    toastCounter += 1;
    setToasts((current) => [...current, { ...entry, id: `export-${String(toastCounter)}` }]);
  };

  const run = (request: DataExportRequest): void => {
    void (async () => {
      setBusy(true);
      setServerError(null);

      const response = await session
        .request('/exports', { method: 'POST', body: JSON.stringify(request) })
        .catch(() => null);

      setBusy(false);
      if (response === null) {
        setServerError(t('error.unavailable'));
        return;
      }
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: { code?: string; details?: { message?: string }[] };
        } | null;
        const code = body?.error?.code ?? 'unavailable';
        setServerError(
          code === 'export_too_large'
            ? /* ⚠️ The row count rides in the detail, so the message says how far over it is rather
               * than "too large" — a number somebody can act on. */
              t('export.error.tooLarge', { rows: body?.error?.details?.[0]?.message ?? '' })
            : code === 'not_found'
              ? t('error.not_found')
              : t('error.unavailable'),
        );
        return;
      }

      const parsed = dataExportResultSchema.safeParse(await response.json());
      if (!parsed.success) {
        setServerError(t('error.unavailable'));
        return;
      }

      setDomain(null);
      if (parsed.data.kind === 'ready') {
        if (parsed.data.xlsx !== undefined) {
          /* ⚠️ Base64 on the wire — a JSON body cannot carry the spreadsheet's bytes raw. */
          const bytes = Uint8Array.from(atob(parsed.data.xlsx), (char) => char.charCodeAt(0));
          saveBlob(new Blob([bytes], { type: XLSX_MIME }), parsed.data.filename);
        } else {
          saveCsv(parsed.data.filename, parsed.data.csv ?? '');
        }
        toast({
          title: t('export.toast.ready'),
          description: t('export.toast.rows', { rows: String(parsed.data.rows) }),
          variant: 'success',
        });
        return;
      }

      /* ⚠️ The address comes from the response, not from the session — the server decided where it
       * goes, and a screen guessing would be guessing wrong the day they differ. */
      toast({
        title: t('export.toast.queued'),
        description: t('export.toast.queuedNote', {
          rows: String(parsed.data.rows),
          email: parsed.data.email,
        }),
        variant: 'success',
      });
    })();
  };

  return {
    dialog: (
      <ExportDialog
        domain={domain}
        reportId={reportId}
        projectId={projectId}
        ids={ids}
        from={seedFrom}
        to={seedTo}
        contractId={contractId}
        memberId={memberId}
        today={today}
        busy={busy}
        serverError={serverError}
        onClose={() => {
          setDomain(null);
          setServerError(null);
        }}
        onRun={run}
      />
    ),
    open: (next, options) => {
      setServerError(null);
      setReportId(options?.reportId ?? null);
      setProjectId(options?.projectId ?? null);
      setIds(options?.ids ?? null);
      setSeedFrom(options?.from ?? null);
      setSeedTo(options?.to ?? null);
      setContractId(options?.contractId ?? null);
      setMemberId(options?.memberId ?? null);
      setDomain(next);
    },
    run,
    toasts,
    dismiss: (id) => {
      setToasts((current) => current.filter((entry) => entry.id !== id));
    },
  };
}
